class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;

    this.gridSelector = this.dataset.grid || '#product-grid';
    this.grid = this.parentElement.querySelector(this.gridSelector);
    if (!this.grid) return;
    this.spinner = this.querySelector('.loading__spinner');
    this.stopped = false;
    this.classList.add('is-active');
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    if (this.dataset.nextUrl) this.observer.observe(this);
    else this.hidden = true;
  }

  disconnectedCallback() {
    this.stopLoading();
  }

  stopLoading() {
    this.stopped = true;
    this.observer?.disconnect();
    this.controller?.abort();
    this.grid?.removeAttribute('aria-busy');
    this.spinner?.classList.add('hidden');
  }

  async loadNextPage() {
    if (this.loading || this.stopped || !this.dataset.nextUrl) return;

    this.loading = true;
    this.controller = new AbortController();
    this.grid.setAttribute('aria-busy', 'true');
    this.spinner.classList.remove('hidden');

    try {
      const url = new URL(this.dataset.nextUrl, window.location.href);
      url.searchParams.set('section_id', this.grid.dataset.id);
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Could not load next page: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = html.querySelector(this.gridSelector);
      const nextPagination = html.querySelector('infinite-scroll');
      if (!nextGrid?.children.length || !nextPagination) throw new Error('Invalid next page');
      if (!this.isConnected || this.stopped) return;

      // Appended cards must remain visible when the theme's reveal animations are enabled.
      nextGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
      this.grid.append(...nextGrid.children);
      this.querySelector('.pagination-wrapper').replaceWith(nextPagination.querySelector('.pagination-wrapper'));
      this.dataset.nextUrl = nextPagination.dataset.nextUrl;

      this.observer.disconnect();
      if (this.dataset.nextUrl) {
        // Observe again so a short page also loads the next batch without another scroll.
        this.observer.observe(this);
      } else {
        this.hidden = true;
      }
    } catch (error) {
      if (error.name !== 'AbortError' && !this.stopped) {
        this.observer.disconnect();
        this.classList.remove('is-active');
      }
    } finally {
      this.loading = false;
      this.grid.removeAttribute('aria-busy');
      this.spinner.classList.add('hidden');
    }
  }
}

if (!customElements.get('infinite-scroll')) customElements.define('infinite-scroll', InfiniteScroll);
